import { api, queryClient } from '@/lib/api-client.ts'
import { AxiosResponse, AxiosError } from 'axios'
import { SingleLabel, SingleLabelGroup, LabelGroups } from '@/routes/settings/labels/types/label.ts'
import { convertFormData, getCurrentOrganization } from '@/lib/utils.ts'
import { useAllLabelGroupStore } from '@/routes/settings/labels/state/allLabels/allLabelGroupStore.ts'

type QuickEditData = {
  name: string | undefined
  icon: string | undefined | null
}

export type CreateLabelData = {
  name: string
  icon: string
  labelGroup: string
}

type EditLabelGroupData = {
  name?: string
  description?: string
}

export const organizationLabelGroupsQuery = ({ projectId }: { projectId: string | undefined }) => ({
  queryKey: [projectId ? 'projectLabelGroups' : 'organizationLabelGroups'],
  queryFn: () => (projectId ? getProjectLabelGroups(projectId) : getLabelGroups()),
})

export const getOrganizationLabelGroups = async ({
  projectId = undefined,
}: {
  projectId: string | undefined
  view?: string
}) => {
  return await queryClient.ensureQueryData(organizationLabelGroupsQuery({ projectId }))
}

export const getProjectLabelGroups = async (projectId: string | undefined) => {
  const { setLabelGroups } = useAllLabelGroupStore.getState()

  if (!projectId) {
    return null
  }
  try {
    const response = await api.get(`/projects/${projectId}/label_groups`)
    const labelGroups = response.data as LabelGroups
    setLabelGroups(labelGroups)
    return labelGroups
  } catch (error) {
    if (error instanceof AxiosError) {
      return null
    }
  }
  return null
}
export const getLabelGroups = async () => {
  const organizationId = getCurrentOrganization()?.organization?.id
  const { setLabelGroups } = useAllLabelGroupStore.getState()

  if (!organizationId) {
    return null
  }
  try {
    const response = await api.get(`/organizations/${organizationId}/label_groups`)
    const labelGroups = response.data as LabelGroups
    setLabelGroups(labelGroups)
    return labelGroups
  } catch (error) {
    if (error instanceof AxiosError) {
      return null
    }
  }
  return null
}

export const editLabel = async (
  label: SingleLabel,
  quickEditData: QuickEditData,
): Promise<AxiosResponse<SingleLabel>> => {
  return api.patch(`/labels/${label.id}`, {
    name: quickEditData.name,
    icon: quickEditData.icon,
  })
}

export const deleteLabel = async (label: SingleLabel): Promise<AxiosResponse<SingleLabel>> => {
  return api.delete(`/labels/${label.id}`)
}

export const createLabel = async (
  createlabelData: CreateLabelData,
): Promise<AxiosResponse<SingleLabel>> => {
  return api.post(`/labels`, createlabelData)
}

export const editLabelGroup = async (
  labelGroup: SingleLabelGroup,
  data: EditLabelGroupData,
): Promise<AxiosResponse<SingleLabelGroup>> => {
  return api.patch(`/label_groups/${labelGroup.id}`, data)
}

export const deleteLabelGroup = async (
  labelGroup: SingleLabelGroup,
): Promise<AxiosResponse<null>> => {
  return api.delete(`/label_groups/${labelGroup.id}`)
}

export const createLabelGroup = async (
  formData: FormData,
): Promise<AxiosResponse<SingleLabelGroup>> => {
  return api.post(`/label_groups`, convertFormData(formData))
}
